import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faHeartPulse, faClock, faBed
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/PatientFlowOptimisation.png";
import vis from "../../assets/images/use-cases/PatientFlowOptimisation-dashboard.png";

const PatientFlowOptimisation = () => {
    return (
        <>
            <SEO
                title="Patient Flow Optimisation: AI Digital Twin for Healthcare | Nebulyx AI"
                description="Reduce hospital wait times by 40% using AI Digital Twins. Simulate patient flow, identify discharge bottlenecks, and test interventions before implementation."
                keywords="AI digital twin, patient flow, healthcare simulation, hospital optimisation, data object graph, NHS, wait times, bed management"
                canonicalUrl="https://nebulyx.ai/use-cases/patient-flow-optimisation"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faHeartPulse}/>
                                            &nbsp;
                                            Healthcare
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;Why are A&E wait times exceeding 6 hours?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Simulate patient journeys through your hospital to identify bottlenecks, optimise resource allocation, and reduce wait times, without disrupting live operations.
                                    </p>
                                </div>
                            </Col>
                        </Row>


                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">40%</span>
                                <span className="stat-label">Wait Time Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faBed} className="stat-icon"/>
                                <span className="stat-value">12 Beds</span>
                                <span className="stat-label">Freed Daily</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">3 Wards</span>
                                <span className="stat-label">Modelled in Hours</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>A&E wait times exceeded 6 hours</li>
                                            <li>Bed occupancy hit 98%</li>
                                            <li>Staff firefighting daily without root cause visibility</li>
                                            <li>Every fix created new bottlenecks elsewhere</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Built Data Object Graph of patient flow</li>
                                            <li>Connected A&E, wards, diagnostics, discharge</li>
                                            <li>AI-powered bottleneck detection</li>
                                            <li>Real-time scenario simulation</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>40% reduction in wait times</li>
                                            <li>12 beds freed daily</li>
                                            <li>Root cause identified in hours not weeks</li>
                                            <li>Confident protocol changes</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Ingests patient arrival, bed occupancy, staff rota, diagnostic queue, and discharge data to map patient journeys and simulate protocol changes.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Sources</strong>
                                    <span>Patient records, bed management, staff rotas, diagnostic systems, discharge data</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Graph</strong>
                                    <span>Data Object Graph maps patient journeys, resource dependencies, and bottleneck points</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Run Scenarios</strong>
                                    <span>What-if engine simulates staffing changes, discharge protocols, capacity shifts</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Optimise Output</strong>
                                    <span>Recommended protocols, resource allocation, and predicted outcomes per scenario</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from A&E arrivals, triage assessments, bed management, staff rotas, diagnostic queues, and discharge records to model patient flow and identify bottlenecks. Simulate protocol changes to predict wait time and bed availability impacts.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Patient Flow Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Tracks patient journeys from A&E to discharge, visualises bottleneck sources across wards, and simulates protocol changes through an interactive wizard guiding clinical teams from problem identification to solution validation.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>A&E Arrival Logs</li>
                                            <li>Triage Assessment Records</li>
                                            <li>Bed Management System</li>
                                            <li>Staff Rota System</li>
                                            <li>Diagnostic Request Queue</li>
                                            <li>Discharge Records</li>
                                            <li>Patient Journey Timeline</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Patient journey mapping</li>
                                            <li>Bottleneck detection</li>
                                            <li>Queue theory modelling</li>
                                            <li>Discharge delay correlation</li>
                                            <li>Capacity stress forecasting</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Add triage nurse</li>
                                            <li>Morning discharge round</li>
                                            <li>Ring-fence assessment beds</li>
                                            <li>Extend pharmacy hours</li>
                                            <li>Add weekend discharges</li>
                                            <li>Redirect minors to UTC</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the full patient flow as a <strong>Data Object Graph</strong>, we discover that implementing a 10am morning discharge round
                                        <strong> reduces average wait times by 40% and frees 12 beds daily</strong>.
                                        Graph traversal reveals that 62% of A&E delays traced back to blocked beds from delayed discharges, not front-door volume.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Root Cause Identified</span>
                                        <span className="discovery-value">62% Discharge Delays</span>
                                        <span className="discovery-note">Not A&E capacity</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Struggling with Patient Flow Visibility?</h2>
                            <p>
                                If your hospital faces mounting wait times and you can't pinpoint root causes,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default PatientFlowOptimisation;